import { useAuth } from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration'
import { Button, BUTTON_TYPES, Modal } from '@src/quince-components/src/atoms'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import ApplePayButton from '../apple-pay-button/apple-pay-button.component'

import styles from './express-pay.module.scss'
import { ExpressPayConfig } from './express-pay.types'

import type { ApplePayConfig } from '../apple-pay-button/apple-pay-button.component'
import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

export interface ExpressPayProps {
  config?: ExpressPayConfig
  productPrice?: number
  className?: string
  /** DS-only visual override forwarded to the Apple Pay button's web-ui `Button` `css` prop. */
  buttonCss?: SystemStyleObject
  dataSelectorKey?: string
  onReady?: () => void
  enableAnimation?: boolean
  disableClick?: boolean
}

export const useIsExpressPayEnabled = () => {
  const deviceInfo = useDeviceInfo()
  const { expressCheckoutEnabled = false } = useStatsigDynamicLocaleConfig()
  return expressCheckoutEnabled && deviceInfo.deviceType === DEVICE_TYPES.MOBILE
}

const ZERO_AMOUNT_MODAL_TITLE_ID = 'zero-amount-modal-title'

const ExpressPay = ({
  config,
  productPrice,
  className,
  buttonCss,
  dataSelectorKey,
  onReady,
  enableAnimation,
  disableClick,
}: ExpressPayProps) => {
  const { isLoggedIn } = useAuth()
  const { t } = useTranslation('common')

  if (!config || config.disabled) {
    return null
  }

  const applePayConfig: ApplePayConfig = {
    getCheckoutDetails: config.getCheckoutDetails,
    validateApplePayClick: config.validateClick,
    onError: config.onError,
    updateShippingAddress: config.updateShippingAddress,
    updateShippingOption: config.updateShippingOption,
    checkoutExpressUpdateDetail: config.checkoutExpressUpdateDetail,
    updateExpressCompleteDetails: config.updateExpressCompleteDetails,
    capture: config.capture,
    skipEmail: isLoggedIn ? true : config.skipEmail,
    disableApplePayExpress: config.disabled,
    getZeroAmountCheckoutUrl: config.getZeroAmountCheckoutUrl,
    showZeroAmountCheckoutModal: config.showZeroAmountCheckoutModal,
  }

  return (
    <>
      <ApplePayButton
        applePayConfig={applePayConfig}
        productPrice={productPrice}
        className={className}
        buttonCss={buttonCss}
        dataSelectorKey={dataSelectorKey}
        onReady={onReady}
        enableAnimation={enableAnimation}
        disableClick={disableClick}
      />
      <Modal
        open={Boolean(config.isZeroAmountModalVisible)}
        onClose={config.handleZeroAmountCheckoutModalClose}
        role="dialog"
        aria-modal={true}
        aria-labelledby={ZERO_AMOUNT_MODAL_TITLE_ID}
        showCloseButton={true}
        closeButtonClassName={styles.zeroAmountCheckoutModalCloseButton}
        className={styles.zeroAmountCheckoutModal}
        modalWrapperClassName={styles.zeroAmountCheckoutModalWrapper}
        variant="bottomSheet"
      >
        <div className={styles.zeroAmountCheckoutModalContent}>
          <h2 id={ZERO_AMOUNT_MODAL_TITLE_ID} className={styles.zeroAmountCheckoutModalTitle}>
            {t('apple_pay_zero_amount_modal_title')}
          </h2>
          <div className={styles.zeroAmountCheckoutModalDescription}>
            {t('apple_pay_zero_amount_modal_description')}
          </div>
          <Button
            type={BUTTON_TYPES.SECONDARY}
            className={styles.zeroAmountCheckoutModalButton}
            onClick={config.handleZeroAmountCheckoutContinue}
          >
            {t('section_renderer.continue_to_checkout')}
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default ExpressPay
